import { useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import type { CompanyMembership } from '../state/auth'
import { useAuth } from '../state/auth'

const navItemsAll = [
  { to: '/', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { to: '/empresas', label: 'Empresas', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
  { to: '/funcionarios', label: 'Funcionários', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  { to: '/custo-funcionario', label: 'Custo do Funcionário', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
  { to: '/rescisao', label: 'Cálculo de Rescisão', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { to: '/relatorios', label: 'Relatórios', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  { to: '/configuracoes', label: 'Configurações', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' }
]

function NavIcon({ d, className }: { d: string; className?: string }) {
  return (
    <svg className={className ?? 'h-5 w-5 shrink-0'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  )
}

function SidebarNav({ items, onNavigate }: { items: typeof navItemsAll; onNavigate?: () => void }) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4">
      <div className="px-2 pb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Menu</div>
      <div className="space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            onClick={() => onNavigate?.()}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm font-medium transition-all duration-200',
                isActive
                  ? 'border-[#616eb3]/30 bg-[#616eb3]/10 text-[#616eb3] shadow-md ring-1 ring-[#616eb3]/20'
                  : 'border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-900 hover:shadow-sm'
              ].join(' ')
            }
          >
            {({ isActive }) => (
              <>
                <NavIcon d={item.icon} className={isActive ? 'h-5 w-5 shrink-0 text-[#616eb3]' : 'h-5 w-5 shrink-0 text-slate-400'} />
                <span>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}

export function AppShell() {
  const { me, selectedCompanyId, setSelectedCompanyId, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const isClient = me?.userType === 'cliente'
  const hideCompanySelector =
    ['/funcionarios', '/custo-funcionario', '/rescisao'].includes(location.pathname) || isClient

  const navItems = useMemo(() => {
    if (!isClient) return navItemsAll
    return navItemsAll.filter((i) => ['/funcionarios', '/custo-funcionario', '/rescisao'].includes(i.to))
  }, [isClient])

  useEffect(() => {
    if (!isClient) return
    const allowed = ['/funcionarios', '/custo-funcionario', '/rescisao']
    if (location.pathname === '/' || !allowed.includes(location.pathname)) {
      navigate('/funcionarios', { replace: true })
    }
  }, [isClient, location.pathname, navigate])

  const memberships = me?.memberships ?? []
  const [mobileOpen, setMobileOpen] = useState(false)
  const canSelectCompany = memberships.length > 0
  const companyOptions = useMemo(() => memberships, [memberships])

  // Bloqueia scroll do body quando menu mobile está aberto
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Sidebar desktop: fixo na tela, menu + Sair sempre visíveis; só a área principal rola */}
      <aside className="fixed left-0 top-0 z-30 hidden h-screen w-72 flex-col border-r border-slate-200 bg-white shadow-lg md:flex">
        <div className="shrink-0 border-b border-[#616eb3]/30 bg-[#616eb3] px-5 py-6 shadow-inner">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow-md">
              <img src="/logo.png" alt="NCC Logo" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="text-base font-bold tracking-tight text-white">NCC Sistema RH</div>
              <div className="text-xs text-white/80">SaaS • Multi-empresa</div>
            </div>
          </div>
        </div>

        <SidebarNav items={navItems} />

        <div className="shrink-0 border-t border-slate-200 bg-slate-50/50 px-3 py-4">
          <button
            className="flex w-full items-center gap-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-left text-sm font-medium text-red-700 shadow-sm transition-all hover:border-red-200 hover:bg-red-100 hover:shadow"
            onClick={() => {
              logout()
              navigate('/login')
            }}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sair
          </button>
        </div>
      </aside>

      <div className="flex min-h-screen md:pl-72">

        {/* Sidebar mobile: menu cobre toda a tela, nada visível atrás */}
        <div className={mobileOpen ? 'fixed inset-0 z-[100] md:hidden' : 'hidden'} aria-hidden={!mobileOpen}>
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/90 backdrop-blur-md"
            onClick={() => setMobileOpen(false)}
            aria-label="Fechar menu"
          />
          <div className="absolute left-0 top-0 flex h-full w-full max-w-[min(90vw,400px)] flex-col overflow-hidden bg-white shadow-2xl">
            <div className="border-b border-[#616eb3]/30 bg-[#616eb3] px-5 py-6 shadow-inner">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow-md">
                    <img src="/logo.png" alt="NCC Logo" className="h-full w-full object-contain" />
                  </div>
                  <div>
                    <div className="text-base font-bold tracking-tight text-white">NCC Sistema RH</div>
                    <div className="text-xs text-white/80">Menu</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/20"
                  onClick={() => setMobileOpen(false)}
                >
                  Fechar
                </button>
              </div>
            </div>
            <SidebarNav items={navItems} onNavigate={() => setMobileOpen(false)} />
            <div className="border-t border-slate-200 bg-slate-50/50 px-3 py-4">
              <button
                className="flex w-full items-center gap-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-left text-sm font-medium text-red-700 shadow-sm transition-all hover:border-red-200 hover:bg-red-100 hover:shadow"
                onClick={() => {
                  setMobileOpen(false)
                  logout()
                  navigate('/login')
                }}
              >
                <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sair
              </button>
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-40 border-b-2 border-[#616eb3]/20 bg-gradient-to-r from-white via-[#616eb3]/10 to-white shadow-md backdrop-blur-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 sm:gap-3 sm:px-4 sm:py-3 md:px-6">
              <div className="flex min-w-0 flex-1 items-center gap-2 sm:flex-initial sm:gap-3">
                <button
                  type="button"
                  className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 md:hidden"
                  onClick={() => setMobileOpen(true)}
                >
                  Menu
                </button>

                <div className="min-w-0 flex-1 md:flex-initial">
                  <div className="truncate text-sm font-semibold text-[#616eb3] md:hidden">NCC RH</div>
                  {!hideCompanySelector && <div className="hidden text-sm font-medium text-[#616eb3] md:block">Empresa</div>}
                </div>

                {!hideCompanySelector && (
                  <select
                    className="h-9 min-w-0 max-w-[50%] flex-1 rounded-lg border-2 border-slate-200 bg-white px-2 text-sm font-medium text-slate-800 transition-colors focus:border-[#616eb3] focus:ring-2 focus:ring-[#616eb3]/30 sm:max-w-none md:w-80 md:flex-none md:px-3"
                    value={selectedCompanyId ?? ''}
                    onChange={(e) => setSelectedCompanyId(e.target.value || null)}
                    disabled={!canSelectCompany}
                  >
                    {companyOptions.length === 0 ? (
                      <option value="">Sem empresas</option>
                    ) : (
                      companyOptions.map((m: CompanyMembership) => (
                        <option key={m.companyId} value={m.companyId}>
                          {m.companyName} ({m.role === 'admin' ? 'Administrador' : 'RH'})
                        </option>
                      ))
                    )}
                  </select>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-2 rounded-lg border border-[#616eb3]/20 bg-[#616eb3]/10 px-2 py-1.5 shadow-sm sm:px-3 sm:py-2">
                <div className="h-2 w-2 shrink-0 rounded-full bg-emerald-500 ring-2 ring-emerald-200" title="Online" />
                <span className="max-w-[40vw] truncate text-xs font-medium text-slate-700 sm:max-w-[55vw] sm:text-sm md:max-w-none">{me?.email ?? ''}</span>
              </div>
            </div>
          </header>

          <main className="flex-1 px-3 py-4 sm:px-4 sm:py-6 md:px-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}
